import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
  TextInput,
  Platform,
  RefreshControl,
  Alert,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import { listUsers, deleteUserAdminWithClerk } from '../services/database';
import LoadingAnimation from '../components/LoadingAnimation';

const ACCOUNT_FILTERS = [
  { value: null, label: 'All' },
  { value: 'PLAYER', label: 'Players' },
  { value: 'CLUB', label: 'Clubs' },
];

const AdminUsersScreen = ({ navigation }) => {
  const { showToast } = useToast();
  const { appUser, clerkUserId } = useAuth();
  const isAdmin = appUser?.isAdmin === true;
  const clerkId = clerkUserId ?? appUser?.clerkId ?? appUser?.clerk_id;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const filters = { limit: 500 };
      if (accountFilter) filters.accountType = accountFilter;
      if (searchQuery.trim()) filters.search = searchQuery.trim();
      const data = await listUsers(filters);
      setUsers(data || []);
    } catch (e) {
      console.error('Error fetching users:', e);
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin, accountFilter, searchQuery]);

  useEffect(() => {
    if (!isAdmin) {
      showToast('Access denied. Admin privileges required.', 'error');
      navigation.goBack();
      return;
    }
  }, [isAdmin]);

  useEffect(() => {
    const unsub = navigation.addListener('focus', () => {
      if (isAdmin) fetchUsers();
    });
    return unsub;
  }, [navigation, isAdmin, fetchUsers]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, fetchUsers]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsers();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const confirmDelete = (u) => {
    const name = u.account_type === 'CLUB' ? (u.club_name || u.username) : u.username;
    const msg = `Delete "${name}" (${u.email})? This cannot be undone.`;
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.confirm) {
      if (window.confirm(msg)) handleDelete(u);
    } else {
      Alert.alert('Delete account', msg, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => handleDelete(u) },
      ]);
    }
  };

  const handleDelete = async (u) => {
    if (!clerkId) {
      showToast('Session expired. Please sign in again.', 'error');
      return;
    }
    try {
      setDeletingId(u.id);
      const targetClerkId = u.clerk_id ?? u.clerkId ?? null;
      await deleteUserAdminWithClerk(u.id, targetClerkId, clerkId);
      showToast(`Deleted ${u.account_type === 'CLUB' ? u.club_name || u.username : u.username}`, 'success');
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) {
      console.error('Error deleting user:', e);
      showToast(e?.message || 'Failed to delete user', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const renderUserCard = ({ item }) => {
    const name = item.account_type === 'CLUB' ? (item.club_name || item.username) : item.username;
    const isSelf = item.id === appUser?.id;
    const isDeleting = deletingId === item.id;

    return (
      <View style={styles.card}>
        <View style={styles.cardMain}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardName} numberOfLines={1}>{name}</Text>
            <View style={[styles.badge, item.account_type === 'CLUB' ? styles.badgeClub : styles.badgePlayer]}>
              <Text style={styles.badgeText}>{item.account_type}</Text>
            </View>
          </View>
          <View style={styles.cardRow}>
            <Icon name="email" size={14} color="#666" />
            <Text style={styles.cardDetail} numberOfLines={1}>{item.email}</Text>
          </View>
          <View style={styles.cardRow}>
            <Icon name="schedule" size={14} color="#666" />
            <Text style={styles.cardDetail}>{formatDate(item.created_at)}</Text>
          </View>
        </View>
        {!isSelf ? (
          <TouchableOpacity
            style={[styles.deleteBtn, isDeleting && styles.deleteBtnDisabled]}
            onPress={() => confirmDelete(item)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <>
                <Icon name="delete-outline" size={18} color="#fff" />
                <Text style={styles.deleteBtnText}>Delete</Text>
              </>
            )}
          </TouchableOpacity>
        ) : (
          <View style={styles.youBadge}>
            <Text style={styles.youBadgeText}>You</Text>
          </View>
        )}
      </View>
    );
  };

  if (!isAdmin) {
    return (
      <View style={styles.centered}>
        <Icon name="lock" size={48} color="#999" />
        <Text style={styles.accessDeniedText}>Admin Access Required</Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Text style={styles.backBtnText}>Go back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation size={375} />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Database</Text>
          <Text style={styles.headerSubtitle}>List and delete users</Text>
        </View>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.searchContainer}>
        <Icon name="search" size={20} color="#666" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search username, email, club…"
          placeholderTextColor="#999"
          value={searchQuery}
          onChangeText={setSearchQuery}
          returnKeyType="search"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Icon name="close" size={20} color="#666" />
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.filterRow}>
        {ACCOUNT_FILTERS.map((f) => (
          <TouchableOpacity
            key={String(f.value)}
            style={[styles.filterTab, accountFilter === f.value && styles.filterTabActive]}
            onPress={() => setAccountFilter(f.value)}
          >
            <Text style={[styles.filterTabText, accountFilter === f.value && styles.filterTabTextActive]}>
              {f.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <FlatList
        data={users}
        keyExtractor={(item) => item.id}
        renderItem={renderUserCard}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Icon name="people-outline" size={56} color="#94EB98" />
            <Text style={styles.emptyText}>No users match your filters</Text>
          </View>
        }
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor="#94EB98" />
        }
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5', padding: 24 },
  accessDeniedText: { fontSize: 18, color: '#333', marginTop: 12 },
  backBtn: { marginTop: 16, paddingVertical: 10, paddingHorizontal: 20, backgroundColor: '#94EB98', borderRadius: 10 },
  backBtnText: { fontSize: 16, fontWeight: '600', color: '#0A0A0A' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: { padding: 4 },
  headerTitleContainer: { flex: 1, marginLeft: 12 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  headerSubtitle: { fontSize: 12, color: '#777', marginTop: 2 },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  searchInput: { flex: 1, paddingVertical: 10, paddingHorizontal: 8, fontSize: 16, color: '#333' },
  filterRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    gap: 10,
  },
  filterTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
  },
  filterTabActive: { backgroundColor: '#94EB98' },
  filterTabText: { fontSize: 14, fontWeight: '500', color: '#666' },
  filterTabTextActive: { color: '#0A0A0A' },
  listContent: { padding: 12, paddingBottom: 100 },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  cardName: { fontSize: 16, fontWeight: '600', color: '#333', flex: 1 },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgePlayer: { backgroundColor: '#94EB9820' },
  badgeClub: { backgroundColor: '#76AF8720' },
  badgeText: { fontSize: 12, fontWeight: '600', color: '#333' },
  cardRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  cardDetail: { fontSize: 14, color: '#666', flex: 1 },
  deleteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F44336',
  },
  deleteBtnDisabled: { opacity: 0.7 },
  deleteBtnText: { fontSize: 14, fontWeight: '600', color: '#fff' },
  youBadge: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: '#e0e0e0' },
  youBadgeText: { fontSize: 13, fontWeight: '600', color: '#666' },
  empty: { alignItems: 'center', paddingVertical: 48 },
  emptyText: { fontSize: 16, color: '#666', marginTop: 12 },
});

export default AdminUsersScreen;
